import { ActionTree } from 'vuex';
import { IActiveDocumentThreshold } from '../types/active-document-threshold';
import { IThresholdState } from './threshold.types';
import {
  EThresholdAction,
  EThresholdGetter,
  EThresholdMutation,
  getDefaultState,
} from './threshold.constants';
import { RootState } from '@/store/store';
import { ThresholdService } from '@/api/threshold/services/threshold.service';
import { translate } from '@/helpers/translation.helper';
import bugsnagClient from '@lib-shared/bugsnag';
import { EnumPlans } from '@/enums/Plans';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';

export const actions: ActionTree<IThresholdState, RootState> = {
  [EThresholdAction.SetShowActiveDocumentThresholdModal]({ commit }, show: boolean) {
    commit(EThresholdMutation.SetShowActiveDocumentThresholdModal, show);
  },
  async [EThresholdAction.EvaluateActiveDocumentThreshold]({ dispatch }, documentId: string) {
    try {
      const {
        hasReachedThreshold,
        threshold,
      } = await ThresholdService.evaluateActiveDocumentThreshold(documentId);
      dispatch(EThresholdAction.EvaluateActiveDocumentThresholdSuccess, {
        hasReachedThreshold,
        threshold,
      });
    } catch (e) {
      dispatch(EThresholdAction.EvaluateActiveDocumentThresholdFailed);
    }
  },
  [EThresholdAction.EvaluateActiveDocumentThresholdSuccess](
    { commit },
    {
      hasReachedThreshold,
      threshold,
    }: { hasReachedThreshold: boolean; threshold: IActiveDocumentThreshold }
  ) {
    commit(EThresholdMutation.SetActiveDocumentThreshold, threshold);
    commit(EThresholdMutation.SetHasReachedActiveDocumentThreshold, hasReachedThreshold);
    commit(EThresholdMutation.SetShowActiveDocumentThresholdModal, hasReachedThreshold);
  },
  [EThresholdAction.EvaluateActiveDocumentThresholdFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-fetch-active-document-threshold'),
      {
        root: true,
      }
    );
  },
  async [EThresholdAction.GetActiveDocumentThreshold]({ dispatch }) {
    try {
      const activeDocumentThreshold: IActiveDocumentThreshold = await ThresholdService.getActiveDocumentThreshold();
      dispatch(EThresholdAction.GetActiveDocumentThresholdSuccess, activeDocumentThreshold);
    } catch (e) {
      dispatch(EThresholdAction.GetActiveDocumentThresholdFailed, e);
    }
  },
  [EThresholdAction.GetActiveDocumentThresholdSuccess](
    { commit },
    activeDocumentThreshold: IActiveDocumentThreshold
  ) {
    commit(EThresholdMutation.SetActiveDocumentThreshold, activeDocumentThreshold);
  },
  [EThresholdAction.GetActiveDocumentThresholdFailed](_, e) {
    if (bugsnagClient) {
      bugsnagClient.notify(e);
    }
  },
  [EThresholdAction.SetShowActiveDocumentThresholdPromptBar]({ commit }, show: boolean) {
    commit(EThresholdMutation.SetShowActiveDocumentThresholdPromptBar, show);
  },
  [EThresholdAction.SetDismissedActiveDocumentThresholdPromptBar]({ commit }, dismissed: boolean) {
    commit(EThresholdMutation.SetDismissedActiveDocumentThresholdPromptBar, dismissed);
  },
  [EThresholdAction.TriggerActiveDocumentThresholdPromptBar]({
    dispatch,
    rootState,
    getters,
    state,
  }) {
    const { plan } = rootState['session'].session;
    const { dismissedActiveDocumentThresholdPromptBar } = state;
    let show = getters[EThresholdGetter.IsNearThresholdLimit];
    if (dismissedActiveDocumentThresholdPromptBar) {
      return;
    }
    if (plan === EnumPlans.free) {
      show = true;
    }
    dispatch(EThresholdAction.SetShowActiveDocumentThresholdPromptBar, show);
  },
  [EThresholdAction.ResetState]({ commit, state }) {
    const newState: IThresholdState = {
      ...getDefaultState(),
      dismissedActiveDocumentThresholdPromptBar: state.dismissedActiveDocumentThresholdPromptBar,
    };
    commit(EThresholdMutation.SetState, newState);
  },
};
